/** ダウンロード用 Blob（MIME を明示） */
export function normalizeDownloadBlob(blob: Blob, mime = 'application/octet-stream'): Blob {
  if (blob.type === mime) return blob;
  return new Blob([blob], { type: mime });
}

export function sanitizeDownloadFilename(filename: string): string {
  return filename.replace(/[/\\?%*:|"<>]/g, '_') || 'download.sql';
}

/**
 * Blob をブラウザのダウンロード一覧向けに保存（ユーザージェスチャー直後の呼び出しが確実）
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const normalized = normalizeDownloadBlob(blob);
  if (normalized.size === 0) {
    throw new Error('ダウンロードファイルが空です。');
  }
  const safeName = sanitizeDownloadFilename(filename);
  const url = URL.createObjectURL(normalized);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  a.rel = 'noopener';
  a.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0.01';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Chrome が blob を読み切る前に revoke すると「サイトでファイルを取得できませんでした」になる
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

/**
 * ブラウザの通常ダウンロード（小さなファイル向け）
 */
export function deliverBlobToUser(blob: Blob, filename: string): void {
  triggerBlobDownload(blob, filename);
}

/** 手動クリック用の object URL（呼び出し側で revoke する） */
export function createBackupManualDownloadUrl(blob: Blob): string {
  return URL.createObjectURL(normalizeDownloadBlob(blob));
}

export function revokeBackupManualDownloadUrl(url: string | null | undefined): void {
  if (url) {
    URL.revokeObjectURL(url);
  }
}

export type DeliverBackupMethod = 'picker' | 'anchor';

export type DeliverBackupResult = {
  method: DeliverBackupMethod;
  manualUrl: string;
  safeFilename: string;
};

/**
 * DB バックアップ保存: File System Access API → プログラム的ダウンロード → 手動リンク（常に返す）
 */
export async function deliverBackupBlob(blob: Blob, filename: string): Promise<DeliverBackupResult> {
  if (blob.size === 0) {
    throw new Error('ダウンロードファイルが空です。');
  }
  const safeFilename = sanitizeDownloadFilename(filename);
  const manualUrl = createBackupManualDownloadUrl(blob);

  const pickerSaved = await saveBlobWithFilePicker(blob, safeFilename);
  if (pickerSaved) {
    return { method: 'picker', manualUrl, safeFilename };
  }

  triggerBlobDownload(blob, safeFilename);
  return { method: 'anchor', manualUrl, safeFilename };
}

/** Chrome / Edge: 保存先ダイアログ（localhost / HTTPS で利用可） */
export async function saveBlobWithFilePicker(blob: Blob, filename: string): Promise<boolean> {
  if (typeof window.showSaveFilePicker !== 'function') {
    return false;
  }
  const safeName = sanitizeDownloadFilename(filename);
  let handle: FileSystemFileHandle;
  try {
    handle = await window.showSaveFilePicker({
      suggestedName: safeName,
      types: [
        {
          description: 'SQL',
          accept: { 'application/sql': ['.sql'], 'application/octet-stream': ['.sql'] },
        },
      ],
    });
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return false;
    }
    throw err;
  }
  const writable = await handle.createWritable();
  await writable.write(normalizeDownloadBlob(blob));
  await writable.close();
  return true;
}

export function openBlobInNewTab(blob: Blob): string {
  const url = URL.createObjectURL(normalizeDownloadBlob(blob));
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    URL.revokeObjectURL(url);
    throw new Error('ポップアップがブロックされました。ブラウザの設定を確認してください。');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
  return url;
}

/**
 * @deprecated DB バックアップは Bearer + Blob（databaseBackup.service）を使用
 */
export function openServerDownloadInNewTab(url: string): void {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.href = url;
  }
}

/**
 * @deprecated DB バックアップは Bearer + Blob（databaseBackup.service）を使用
 */
export function triggerServerFileDownload(url: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = 'download';
  iframe.style.cssText =
    'position:absolute;width:0;height:0;border:0;visibility:hidden;pointer-events:none';
  const frameName = `kensuke-dl-${Date.now()}`;
  iframe.name = frameName;
  document.body.appendChild(iframe);

  const a = document.createElement('a');
  a.href = url;
  a.target = frameName;
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  window.setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, 600_000);
}

export function formatBackupFilename(prefix = 'kensuke_backup'): string {
  const stamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[-:T]/g, '')
    .replace(/-/g, '');
  return `${prefix}_${stamp}.sql`;
}
